import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, MessageCircle, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { api, resolveImage, whatsappLink } from "@/lib/api";
import { COUNTRIES } from "@/lib/countries";
import { ImageUpload } from "@/components/admin/ImageUpload";
import { Modal, Field, TextInput, Toggle, PrimaryButton } from "@/components/admin/ui";

const empty = { name: "", reseller_id: "", country: "", country_code: "", whatsapp_number: "", image_url: "", active: true, order: 0 };

export default function ManageResellers() {
  const qc = useQueryClient();
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const { data: items } = useQuery({ queryKey: ["admin-resellers"], queryFn: async () => (await api.get("/admin/resellers")).data });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["admin-resellers"] });
    qc.invalidateQueries({ queryKey: ["resellers"] });
    qc.invalidateQueries({ queryKey: ["resellers-preview"] });
    qc.invalidateQueries({ queryKey: ["reseller-countries"] });
    qc.invalidateQueries({ queryKey: ["admin-stats"] });
  };

  const save = useMutation({
    mutationFn: async (data) =>
      editing ? api.put(`/admin/resellers/${editing}`, data) : api.post("/admin/resellers", data),
    onSuccess: () => { invalidate(); setModal(false); toast.success(editing ? "Reseller updated" : "Reseller added"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Failed to save"),
  });

  const del = useMutation({
    mutationFn: async (id) => api.delete(`/admin/resellers/${id}`),
    onSuccess: () => { invalidate(); toast.success("Reseller deleted"); },
  });

  const openAdd = () => { setForm({ ...empty, order: (items?.length || 0) }); setEditing(null); setModal(true); };
  const openEdit = (r) => { setForm(r); setEditing(r.id); setModal(true); };

  const onCountry = (name) => {
    const match = COUNTRIES.find((c) => c.name === name);
    setForm((f) => ({ ...f, country: name, country_code: match ? match.code : f.country_code }));
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-white">Resellers</h1>
          <p className="mt-1 text-slate-400">Add and manage your official recharge resellers.</p>
        </div>
        <PrimaryButton onClick={openAdd} testid="add-reseller-btn"><Plus className="h-5 w-5" /> Add</PrimaryButton>
      </div>

      <div className="mt-8 space-y-3">
        {(items || []).map((r) => (
          <div key={r.id} className="card-surface flex items-center gap-4 rounded-xl p-4" data-testid={`admin-reseller-${r.id}`}>
            <div className="h-14 w-14 shrink-0 overflow-hidden rounded-full ring-2 ring-gold/30">
              {r.image_url ? <img src={resolveImage(r.image_url)} alt="" className="h-full w-full object-cover" />
                : <div className="flex h-full w-full items-center justify-center bg-obsidian-elevated font-display text-xl gold-text">{r.name?.[0]}</div>}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-bold text-slate-100">{r.name}</p>
                <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${r.active ? "bg-whatsapp/15 text-whatsapp" : "bg-slate-600/30 text-slate-400"}`}>
                  {r.active ? "Active" : "Inactive"}
                </span>
              </div>
              <p className="truncate text-sm text-slate-400">
                {r.country} {r.reseller_id && `· ID ${r.reseller_id}`} · +{r.country_code}{r.whatsapp_number}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <a href={whatsappLink(r.country_code, r.whatsapp_number, "Test")} target="_blank" rel="noreferrer"
                className="rounded-lg p-2 text-whatsapp hover:bg-whatsapp/10" title="Test WhatsApp"><MessageCircle className="h-5 w-5" /></a>
              <button onClick={() => openEdit(r)} className="rounded-lg p-2 text-gold hover:bg-gold/10" data-testid={`edit-reseller-${r.id}`}><Pencil className="h-5 w-5" /></button>
              <button onClick={() => { if (window.confirm("Delete this reseller?")) del.mutate(r.id); }}
                className="rounded-lg p-2 text-red-400 hover:bg-red-500/10" data-testid={`delete-reseller-${r.id}`}><Trash2 className="h-5 w-5" /></button>
            </div>
          </div>
        ))}
        {items?.length === 0 && <p className="py-12 text-center text-slate-500">No resellers yet. Add your first one.</p>}
      </div>

      <Modal open={modal} onClose={() => setModal(false)} title={editing ? "Edit Reseller" : "Add Reseller"} testid="reseller-modal">
        <form onSubmit={(e) => { e.preventDefault(); save.mutate(form); }} className="space-y-4">
          <ImageUpload value={form.image_url} onChange={(url) => setForm((f) => ({ ...f, image_url: url }))} label="Profile Picture" testid="reseller-image" />
          <Field label="Reseller Name">
            <TextInput value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required testid="reseller-name-input" />
          </Field>
          <Field label="Reseller ID (optional)">
            <TextInput value={form.reseller_id} onChange={(e) => setForm((f) => ({ ...f, reseller_id: e.target.value }))} testid="reseller-id-input" />
          </Field>
          <Field label="Country">
            <input list="country-list" value={form.country} onChange={(e) => onCountry(e.target.value)}
              data-testid="reseller-country-input" placeholder="Select or type a country"
              className="h-11 w-full rounded-lg border border-gold/20 bg-obsidian px-3 text-slate-100 outline-none focus:border-gold/60" />
            <datalist id="country-list">{COUNTRIES.map((c) => <option key={c.name} value={c.name} />)}</datalist>
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Country Code">
              <TextInput value={form.country_code} onChange={(e) => setForm((f) => ({ ...f, country_code: e.target.value.replace(/\D/g, "") }))}
                placeholder="880" required testid="reseller-code-input" />
            </Field>
            <div className="col-span-2">
              <Field label="WhatsApp Number (no country code)">
                <TextInput value={form.whatsapp_number} onChange={(e) => setForm((f) => ({ ...f, whatsapp_number: e.target.value.replace(/\D/g, "") }))}
                  placeholder="1700000000" required testid="reseller-whatsapp-input" />
              </Field>
            </div>
          </div>
          <p className="rounded-lg bg-obsidian px-3 py-2 text-xs text-slate-400">
            WhatsApp link preview: <span className="font-mono text-gold">wa.me/{form.country_code}{form.whatsapp_number}</span>
          </p>
          <div className="grid grid-cols-2 items-center gap-3">
            <Field label="Display Order">
              <TextInput type="number" value={form.order} onChange={(e) => setForm((f) => ({ ...f, order: Number(e.target.value) }))} testid="reseller-order-input" />
            </Field>
            <div className="pt-6">
              <Toggle checked={form.active} onChange={(v) => setForm((f) => ({ ...f, active: v }))} label={form.active ? "Active" : "Inactive"} testid="reseller-active-toggle" />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setModal(false)} className="rounded-lg px-5 py-2.5 text-sm font-semibold text-slate-300 hover:bg-white/5">Cancel</button>
            <PrimaryButton type="submit" disabled={save.isPending} testid="save-reseller-btn">{save.isPending ? "Saving..." : "Save"}</PrimaryButton>
          </div>
        </form>
      </Modal>
    </div>
  );
}
